import { useLayoutEffect } from 'react';
import { useCursor } from './CursorContext';

interface CursorNavigateConnectorProps {
  /**
   * Called once when the navigate function is available.
   * The class component stores the received function and uses it to drive the
   * cursor without going through React state — the only escape hatch for a
   * class that lives outside the hooks call stack.
   */
  onNavigateReady: (navigate: (branchId: number, stepIndex: number | null) => void) => void;
  onResetReady: (reset: () => void) => void;
}

/**
 * Bridges the CursorContext into the class component that owns StoryBranches.
 * Must be rendered inside CursorProvider. Renders nothing — side-effect only.
 */
export function CursorNavigateConnector({ onNavigateReady, onResetReady }: CursorNavigateConnectorProps) {
  const { navigate, resetNavigationHistory } = useCursor();

  // useLayoutEffect (not useEffect) so the callback fires in the commit phase,
  // before the parent class component's componentDidMount. React runs layout
  // effects of children before parents in the commit phase, which guarantees
  // _cursorNavigate is set before loadBranchList triggers the first navigation
  // call — closing the window where the initial frame would be silently lost.
  useLayoutEffect(() => {
    onNavigateReady(navigate);
    onResetReady(resetNavigationHistory);
  }, [onNavigateReady, navigate, onResetReady, resetNavigationHistory]);

  return null;
}
